import React, { type ComponentType, Component, memo } from 'react';
import type { PreloadedState, Store } from 'redux';
import deepEquals from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import type { PreloadedQuery } from 'react-relay';
import type { RelayObservable } from 'relay-runtime/lib/network/RelayObservable';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SpotlightManager } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
/* eslint jira/import/gql-multi-client: off */
import {
	ApolloClientProvider,
	getClient,
} from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import swagClient from '@atlassian/jira-apollo-swag/src/index.tsx';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import AppStyle from '@atlassian/jira-common-components-app-style/src/index.tsx';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { measureFunc, setMark, setMeasure } from '@atlassian/jira-common-performance/src/marks.tsx';
import { disposableEpic } from '@atlassian/jira-common-redux-disposable/src/controllers/disposable-epic/index.tsx';
import { DISPOSE_ACTION } from '@atlassian/jira-common-redux-disposable/src/index.tsx';
import isShallowEqual from '@atlassian/jira-common-util-is-shallow-equal/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FlagsDispatcher } from '@atlassian/jira-flags';
import MemoryObserver from '@atlassian/jira-memory-metrics/src/ui/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	jswCreateReduxStoreEndMark,
	jswCreateReduxStoreStartMark,
	jswRenderStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type { useVersionsRefetchQuery$data } from '@atlassian/jira-relay/src/__generated__/useVersionsRefetchQuery.graphql';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types.tsx';
import type { JiraPlanDataRestResponse } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import {
	BACKLOG,
	RESOURCES_CACHE_UPDATER_SOURCE,
	RESOURCES_CACHE_UPDATER_SOURCE_KEY,
} from '@atlassian/jira-software-resource-invalidator/src/common/types.tsx';
import { triggerCacheRefreshAnalytics } from '@atlassian/jira-software-resource-invalidator/src/controllers/resources-cache-refresh-analytics-subject/index.tsx';
import ResourcesCacheWrapper from '@atlassian/jira-software-resource-invalidator/src/ui/resources-cache-wrapper/index.tsx';
import type { UIFBacklogCachedDataResult } from '@atlassian/jira-software-uif-early-script/src/index.tsx';
import {
	type RouterActionReplace,
	type Location,
	type LocationShape,
	type RouterActionPush,
	withRouter,
} from '@atlassian/react-resource-router';
import { useStoreFields } from './common/hooks/use-store-fields/main.tsx';
import type { StoreFieldsActions } from './common/hooks/use-store-fields/types.tsx';
import { useVersionsRefetch } from './common/hooks/use-versions-refetch/index.tsx';
import { isBacklogWithRelayEnabled } from './feature-flags.tsx';
import { flagsMapper } from './flags/index.tsx';
import { APP_ID, REFRESH_SOURCE_ON_MOUNT, TEAM_NAME, PACKAGE_NAME } from './model/constants.tsx';
import type { ConfigProps, GlobalIssueCreateProps } from './model/types.tsx';
import createRootEpic from './ops/index.tsx';
import {
	getHideEpics,
	getHideStatuses,
	getHideVersions,
} from './services/local-storage/global-storage.tsx';
import {
	getBacklogCardGroupSettings,
	getBacklogSettings,
	getWorkloadSubtaskSettings,
} from './services/local-storage/index.tsx';
import { removeLocationLegacyPanelQueryParameters } from './services/routing/legacy-panel-query-parameters/index.tsx';
import { getCustomDataForMemoryAnalyticsFromUrl } from './services/utils/memory-analytics/index.tsx';
import {
	nextGenBacklogPageLoad,
	PAGE_LOAD_MARK_CONSTRUCTOR_END,
	PAGE_LOAD_MARK_CONSTRUCTOR_START,
	PAGE_LOAD_MARK_REDUX_END,
	PAGE_LOAD_MARK_REDUX_START,
	PAGE_LOAD_MARK_RENDER_START,
	uifBacklogNewUiPageLoad,
} from './services/utils/performance-analytics/index.tsx';
import {
	initializeAppProps,
	loadAppData,
	loadConfigData,
	refreshDataRequest,
} from './state/actions/app/index.tsx';
import {
	setExpandedCardGroups,
	setCollapsedCardLists,
	setCollapsedCardGroups,
} from './state/actions/card-list/index.tsx';
import {
	toggleCardEpicFields,
	toggleCardStatusFields,
	toggleCardVersionFields,
} from './state/actions/cards/fields/index.tsx';
import { loadPriorities } from './state/actions/priorities/index.tsx';
import { requestCurrentUser } from './state/actions/user/index.tsx';
import { setIncludeSubtasksAction } from './state/actions/workload-modal/index.tsx';
import {
	type CreateStoreOptions,
	BacklogReduxProvider,
	createStoreWithCache,
	unmountCachedStore,
} from './state/index.tsx';
import type { State } from './state/types.tsx';
import DataProviders from './view/data-providers/index.tsx';
import GICOnCloseHandler from './view/gic-on-close-handler/index.tsx';
import View from './view/index.tsx';
import SLAExperienceTracker from './view/sla-experience-tracker/index.tsx';

export type Props = {
	isCacheHit: boolean;
	isServer: boolean;
	isCompanyManaged: boolean;
	// eslint-disable-next-line jira/react/handler-naming
	bindToOnJiraGlobalIssueCreateClose: (
		arg1: (event: Event, issues: GlobalIssueCreateProps[]) => void,
	) => void;
	locale: Locale;
	baseUrl: string;
	rapidViewId: string;
	xsrfToken: string;
	projectKey: string;
	projectId: number;
	projectName: string;
	cloudId: string;
	atlassianAccountId: string | null;
	prefetchedData: PrefetchedDataGql | null;
	prefetchedAggData: aggBacklogDataQuery$data | null;
	/**
	 * @deprecated Pass the promise in to start rendering from cache.
	 */
	prefetchedJiraPlanRestData?: JiraPlanDataRestResponse | null;
	/**
	 * @deprecated Remove when clean up indexeddb_cache_for_backlog
	 */
	prefetchedJiraPlanRestDataPromise?: Promise<JiraPlanDataRestResponse | null> | null;
	prefetchCmpBacklogData?: UIFBacklogCachedDataResult | null;
	prefetchCmpBacklogDataPromise?: Promise<UIFBacklogCachedDataResult | null> | null;
	push: RouterActionPush;
	replace: RouterActionReplace;
	location: Location;
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null | undefined;
	versionFilterRelayQueryRef: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null | undefined;
	getDataFromAGG?: () => Promise<aggBacklogDataQuery$data>;
	// eslint-disable-next-line jira/react/handler-naming
	refetchVersionsForProjects?: (
		jiraProjectIds: string[],
	) => RelayObservable<useVersionsRefetchQuery$data>;
	/**
	 * Disables redux store caches, used for integration testing.
	 */
	disableStoreCaches?: boolean;
	storeFieldsActions?: StoreFieldsActions;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	/**
	 * Used for testing purposes only
	 */
	initialState?: State;
};

/**
 * Set performance marks for rendering performance
 */
function measureRender() {
	setMark('render:start');
	requestAnimationFrame(() => {
		setMark('render:end');
		setMeasure('render', 'render:start', 'render:end');
	});
}

// eslint-disable-next-line jira/react/no-class-components
class Backlog extends Component<Props> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static defaultProps = {
		isServer: false,
		isCompanyManaged: false,
		initialState: {},
	};

	static getAppPropsFromProps(props: Props): ConfigProps {
		return {
			locale: props.locale,
			baseUrl: props.baseUrl,
			rapidViewId: props.rapidViewId,
			isCompanyManaged: props.isCompanyManaged ?? false,
			xsrfToken: props.xsrfToken,
			projectKey: props.projectKey,
			projectId: props.projectId,
			projectName: props.projectName,
			cloudId: props.cloudId,
			atlassianAccountId: props.atlassianAccountId,
		};
	}

	constructor(props: Props) {
		super(props);

		const { isCompanyManaged } = props;

		const metric = isCompanyManaged ? uifBacklogNewUiPageLoad : nextGenBacklogPageLoad;

		metric.mark(PAGE_LOAD_MARK_CONSTRUCTOR_START);

		metric.mark(PAGE_LOAD_MARK_REDUX_START);
		const rootEpic = createRootEpic(
			props.isServer,
			props.isCompanyManaged,
			() => this.props.location,
			props.push,
			props.replace,
			getClient(props.baseUrl, swagClient, props.xsrfToken),
		);

		setMark(jswCreateReduxStoreStartMark);
		const createStoreOptions: CreateStoreOptions = {
			rootEpic: disposableEpic(rootEpic),
			dependencies: {
				isCompanyManaged,
				fetchDataFromAGG: props.getDataFromAGG,
				storeFieldsActions: props.storeFieldsActions,
				refetchVersionsForProjects: props.refetchVersionsForProjects,
				createAnalyticsEvent: props.createAnalyticsEvent,
			},
		};

		this.store = createStoreWithCache(
			props.rapidViewId,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			props.initialState ?? ({} as PreloadedState<State>),
			{
				...createStoreOptions,
				disableStoreCaches: props.disableStoreCaches ?? false,
			},
		);
		setMark(jswCreateReduxStoreEndMark);
		setMeasure('create-redux-store', jswCreateReduxStoreStartMark, jswCreateReduxStoreEndMark);

		this.store.dispatch(initializeAppProps(Backlog.getAppPropsFromProps(props)));

		// Fire multiple requests to get data for the board. GraphQL please save us.
		measureFunc('loadAppData', () => {
			if (props.isCompanyManaged) {
				if (fg('indexeddb_cache_for_backlog')) {
					if (props.prefetchCmpBacklogDataPromise) {
						props.prefetchCmpBacklogDataPromise.then((data) => {
							this.store.dispatch(loadAppData({ prefetchCmpBacklogData: data }));
						});
					} else {
						this.store.dispatch(loadAppData());
					}
				} else if (props.prefetchedJiraPlanRestDataPromise) {
					// TODO: Handle error
					props.prefetchedJiraPlanRestDataPromise.then((data) => {
						this.store.dispatch(loadAppData({ prefetchedJiraPlanRestData: data }));
					});
				} else if (props.prefetchedJiraPlanRestData) {
					this.store.dispatch(
						loadAppData({
							prefetchedJiraPlanRestData: props.prefetchedJiraPlanRestData,
						}),
					);
				} else {
					this.store.dispatch(loadAppData());
				}
			} else if (isBacklogWithRelayEnabled() && props.prefetchedAggData) {
				this.store.dispatch(loadAppData({ prefetchedAggData: props.prefetchedAggData }));
			} else if (props.prefetchedData) {
				this.prefetchedData = props.prefetchedData;
				this.store.dispatch(loadAppData({ prefetchedData: props.prefetchedData }));
			} else {
				this.store.dispatch(loadAppData());
			}
		});

		measureFunc('loadConfigData', () => {
			this.store.dispatch(loadConfigData());
			this.store.dispatch(loadPriorities());

			// This isn't required on the server, so it shouldn't be requested.
			if (!__SERVER__) {
				this.store.dispatch(requestCurrentUser());
			}
		});

		measureFunc('setCollapsedCardLists', () => {
			const lists = getBacklogSettings(Number(props.rapidViewId)).collapsedCardLists;
			if (lists) {
				const listsIds = Object.keys(lists);
				if (listsIds.length > 0) {
					this.store.dispatch(setCollapsedCardLists(listsIds));
				}
			}
		});
		measureFunc('setCollapsedCardGroups', () => {
			const groups = getBacklogCardGroupSettings(Number(props.rapidViewId));
			if (groups) {
				const groupIds = Object.keys(groups);
				if (groupIds.length > 0) {
					this.store.dispatch(setCollapsedCardGroups(groupIds));
				}
			}
		});
		measureFunc('setExpandedCardGroups', () => {
			const groups = getBacklogCardGroupSettings(Number(props.rapidViewId));
			if (groups) {
				const groupIds = Object.keys(groups);
				if (groupIds.length > 0) {
					this.store.dispatch(setExpandedCardGroups(groupIds));
				}
			}
		});

		measureFunc('toggleHiddenLabels', () => {
			if (isCompanyManaged) {
				const willHideEpicLabels = getHideEpics(props.rapidViewId);
				this.store.dispatch(toggleCardEpicFields(willHideEpicLabels));
				const willToggleStatusLabels = getHideStatuses(props.rapidViewId);
				this.store.dispatch(toggleCardStatusFields(willToggleStatusLabels));
				const willToggleVersionLabels = getHideVersions(props.rapidViewId);
				this.store.dispatch(toggleCardVersionFields(willToggleVersionLabels));
			}
		});

		measureFunc('setIncludeSubtasksInWorkloadModal', () => {
			if (isCompanyManaged) {
				const includeSubtasksLocal = getWorkloadSubtaskSettings();
				this.store.dispatch(setIncludeSubtasksAction(includeSubtasksLocal));
			}
		});

		metric.mark(PAGE_LOAD_MARK_REDUX_END);
		metric.mark(PAGE_LOAD_MARK_CONSTRUCTOR_END);
	}

	componentDidMount() {
		this.hasRendered = true;
		removeLocationLegacyPanelQueryParameters(this.props.location, this.props.push);

		if (isBacklogWithRelayEnabled() && this.props.isCacheHit) {
			this.store.dispatch(refreshDataRequest(REFRESH_SOURCE_ON_MOUNT));
		}
	}

	componentDidUpdate(prevProps: Props) {
		const prevAppProps = Backlog.getAppPropsFromProps(prevProps);
		const appProps = Backlog.getAppPropsFromProps(this.props);

		// If our resource cache data is different, load it up!!!
		// We fire a refresh on cached mount and it will proc this lifecycle event
		// See src/packages/spa-apps/nextgen-backlog/src/resources-cache-refresh
		if (
			!isBacklogWithRelayEnabled() &&
			!deepEquals(prevProps.prefetchedData, this.props.prefetchedData)
		) {
			// if this has a source key, and the key is the one we get from the resources
			// cache updater. Don't process it.
			if (
				!isNil(this.props.prefetchedData) &&
				(!(RESOURCES_CACHE_UPDATER_SOURCE_KEY in this.props.prefetchedData) ||
					this.props.prefetchedData[RESOURCES_CACHE_UPDATER_SOURCE_KEY] !==
						RESOURCES_CACHE_UPDATER_SOURCE)
			) {
				// fires an analytics event to track that the cache is stale and will be updated
				triggerCacheRefreshAnalytics(BACKLOG);
				this.store.dispatch(loadAppData({ prefetchedData: this.props.prefetchedData }));
			}
		}

		if (isShallowEqual(prevAppProps, appProps)) {
			return;
		}

		this.store.dispatch(initializeAppProps(appProps));
	}

	componentWillUnmount() {
		if (this.store && this.store.dispatch) {
			this.store.dispatch(DISPOSE_ACTION);
		}

		unmountCachedStore();
	}

	store: Store<State>;

	prefetchedData: PrefetchedDataGql | null = null;

	hasRendered = false;

	onGetLocation = (): Location => this.props.location;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onPush = (path: LocationShape, state: any) => {
		this.props.push(path, state);
	};

	renderContent() {
		const {
			rapidViewId,
			isCacheHit,
			customFiltersQueryRef,
			versionFilterRelayQueryRef,
			prefetchedAggData,
			prefetchedJiraPlanRestData,
			prefetchCmpBacklogData,
		} = this.props;

		const inner = (
			<SLAExperienceTracker>
				<SpotlightManager>
					<View
						isCacheHit={isCacheHit}
						prefetchedData={this.prefetchedData}
						prefetchedAggData={prefetchedAggData}
						prefetchedPlanData={
							fg('indexeddb_cache_for_backlog')
								? prefetchCmpBacklogData?.result
								: prefetchedJiraPlanRestData
						}
						getLocation={this.onGetLocation}
						push={this.onPush}
						customFiltersQueryRef={customFiltersQueryRef}
						versionFilterRelayQueryRef={versionFilterRelayQueryRef}
					/>
				</SpotlightManager>
			</SLAExperienceTracker>
		);

		const AppStyleComponent =
			getWillShowNav4() && fg('jira_nav4_eap_drop_2') ? ContentAppStyle : AppStyle;

		return (
			<AppStyleComponent>
				<DataProviders boardId={Number(rapidViewId)}>{inner}</DataProviders>
				<FlagsDispatcher mapper={flagsMapper} />
				<ResourcesCacheWrapper origin={BACKLOG} />
				<MemoryObserver getCustomData={getCustomDataForMemoryAnalyticsFromUrl} />
			</AppStyleComponent>
		);
	}

	render() {
		const { isCompanyManaged } = this.props;
		const metric = isCompanyManaged ? uifBacklogNewUiPageLoad : nextGenBacklogPageLoad;

		if (!this.hasRendered) {
			setMark(jswRenderStartMark);
			metric.mark(PAGE_LOAD_MARK_RENDER_START);

			measureRender();
		}

		return (
			<AppBase
				id={APP_ID}
				teamName={TEAM_NAME}
				packageName={PACKAGE_NAME}
				store={this.store}
				fallback="page"
			>
				<BacklogReduxProvider store={this.store}>
					<GICOnCloseHandler
						bindToOnJiraGlobalIssueCreateClose={this.props.bindToOnJiraGlobalIssueCreateClose}
					/>
					<ApolloClientProvider client={swagClient}>{this.renderContent()}</ApolloClientProvider>
				</BacklogReduxProvider>
			</AppBase>
		);
	}
}

const BacklogWrapper = memo((props: Props) => {
	const storeFieldsActions = useStoreFields();
	const refetchVersions = useVersionsRefetch();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (
		<Backlog
			{...props}
			storeFieldsActions={storeFieldsActions}
			refetchVersionsForProjects={refetchVersions}
			createAnalyticsEvent={createAnalyticsEvent}
		/>
	);
});

// withRouter types are wrong
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const BacklogWithRouter = withRouter(BacklogWrapper) as ComponentType<
	Omit<Props, 'push' | 'replace' | 'location'>
>;
export default BacklogWithRouter;

const ContentAppStyle = ({ children }: { children: React.ReactNode }) => (
	<Box xcss={contentStyle}>{children}</Box>
);

const contentStyle = xcss({
	font: 'font.body',
	height: 'inherit',
	letterSpacing: 'normal',
	display: 'contents',
});
